<template>
    <div class="notification-panel d-flex flex-column h-100">
        <div class="d-flex justify-content-between pb16 border-b gap-3">
            <CustomSelectButton v-model="selectedTab" :options="notificationSections" />
            <Button 
                v-if="selectedTab === 'new'" 
                type="btn-outline-grey" 
                class=" d-flex gap-2 align-items-center justify-content-center" 
                @mouseover="iconHover = 'double-check'" 
                @mouseleave="iconHover = false"
                :disabled="!$store.getters['informator/unreadNotifications'].length"
                :loader="bulkLoading"
                @click="markAllAsRead"
            >
                <Icon
                    v-if="!isMobile && !bulkLoading"
                    name="double-check"
                    size="c20"
                    :active="iconHover === 'double-check'"
                />
                <span class="text-nowrap">{{ $t('components.service.Informator.1') }}</span>
            </Button>
            <Button 
                v-else 
                type="btn-outline-red" 
                class=" d-flex gap-2 align-items-center justify-content-center" 
                @mouseover="iconHover = 'trash'" 
                @mouseleave="iconHover = false"
                :disabled="!$store.getters['informator/readNotifications'].length"
                :loader="bulkLoading"
                @click="deleteAllNotifications"
            >
                <Icon
                    v-if="!isMobile && !bulkLoading"
                    name="trash"
                    size="c20"
                    :active="iconHover === 'trash'"
                />
                <span class="text-nowrap">{{ $t('components.service.Informator.2') }}</span>
            </Button>
        </div>
        <div v-if="notificationsListLength">
            <!-- Непрочитанные -->
            <div v-if="selectedTab === 'new'">
                <div v-for="notification in $store.getters['informator/unreadNotifications']" :key="notification.code" class="notification-item">
                    <div class="d-flex align-items-center gap-3">
                        <span class="notification-circle" :style="{ backgroundColor: getIconBgColor(notification.group) }">
                            <Icon :name="icons[notification.group]" size="c20" />
                        </span>
                        <div class="d-flex flex-column flex-grow-1 gap-1">
                            <div class="d-flex justify-content-between align-items-center">
                                <div class="fsp16 lh24 neue">{{ titles[notification.group] }}</div>
                                <div class="fsp12 lh16 text-nowrap neue-roman">{{ getRelativeTime(notification.ts_create) }}</div>
                            </div>
                            <div class="d-flex justify-content-between">
                                <div class="fsp14 primary-grey lh20 neue-roman">{{ notificationBody(notification) }}</div>
                                <Button
                                    type="btn-outline-grey"
                                    class="action-btn"
                                    :loader="pendingCodes.includes(notification.code)"
                                    :loaderSize="18"
                                    @mouseover="iconHover = `double-check-${notification.code}`"
                                    @mouseleave="iconHover = false" @click="markAsRead(notification.code)"
                                >
                                    <Icon
                                        v-if="!isMobile && !pendingCodes.includes(notification.code)"
                                        name="double-check"
                                        size="c20"
                                        :active="iconHover === `double-check-${notification.code}`"
                                    />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Прочитанные -->
            <div v-else>
                <div v-for="notification in $store.getters['informator/readNotifications']" :key="notification.code" class="notification-item">
                    <div class="d-flex align-items-center gap-3">
                        <span class="notification-circle" :style="{ backgroundColor: getIconBgColor(notification.group) }">
                            <Icon :name="icons[notification.group]" size="c20" />
                        </span>
                        <div class="d-flex flex-column flex-grow-1 gap-1">
                            <div class="d-flex justify-content-between align-items-center">
                                <div class="fsp16 lh24 neue primary-grey">{{ titles[notification.group] }}</div>
                                <div class="fsp12 lh16 primary-grey text-nowrap neue-roman">{{ getRelativeTime(notification.ts_create) }}</div>
                            </div>
                            <div class="d-flex justify-content-between">
                                <div class="fsp14 primary-grey lh20 neue-roman">{{ notificationBody(notification) }}</div>
                                <Button
                                    type="btn-outline-red"
                                    class="action-btn"
                                    :loader="pendingCodes.includes(notification.code)"
                                    :loaderSize="18"
                                    @mouseover="iconHover = `trash-${notification.code}`"
                                    @mouseleave="iconHover = false"
                                    @click="deleteNotification(notification.code)"
                                >
                                    <Icon
                                        v-if="!isMobile && !pendingCodes.includes(notification.code)"
                                        name="trash"
                                        size="c20"
                                        :active="iconHover === `trash-${notification.code}`"
                                    />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class='grey text-center background-empty h-100 d-flex align-items-center justify-content-center px16 flex-grow-1'>
            <div class="d-flex flex-column align-items-center">
                <div class="icon-background">
                    <Icon name="mail-check" size="c24" />
                </div>
                <h5 class="fsp20 lh28 mb-0 color-menu neue mb8">{{ $t('components.service.Informator.3') }}</h5>
                <div class="fsp14 lh20 primary-grey text-center neue-roman">
                    <div>{{ $t('components.service.Informator.4') }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CustomSelectButton from './CustomSelectButton.vue';
import Icon from '@/components/icons/Icon';
import Button from './Button.vue';

export default {
    components: {
        CustomSelectButton,
        Icon,
        Button
    },
    data() {
        return {
            selectedTab: 'new',
            iconHover: false,
            pendingCodes: [],
            bulkLoading: false,
            notifications: [],
            icons: {
                'auth': 'inform-auth',
                'card': 'inform-card',
                'outcome': 'inform-outcome',
                'income': 'inform-income',
                'security': 'inform-security',
                'mail': 'inform-mail',
                'payrequest': 'inform-payrequest',
                'shop_changes': 'inform-shop',
                'shop_admin': 'inform-shop',
            },
            iconColors: {
                'auth': '#3B82F6',
                'card': '#0284C7',
                'outcome': '#F97316',
                'income': '#10B981',
                'security': '#4F46E5',
                'mail': '#0EA5E9',
                'payrequest': '#EAB308',
                'shop_changes': '#8B5CF6',
                'shop_admin': '#8B5CF6',
            },
            titles: {
                'auth': this.$t('components.service.Informator.12'),
                'card': this.$t('components.service.Informator.13'),
                'outcome': this.$t('components.service.Informator.14'),
                'income': this.$t('components.service.Informator.15'),
                'security': this.$t('components.service.Informator.16'),
                'mail': this.$t('components.service.Informator.17'),
                'payrequest': this.$t('components.service.Informator.18'),
                'shop_changes': this.$t('components.service.Informator.27'),
                'shop_admin': this.$t('components.service.Informator.27')
            },
            currentTimestamp: Date.now(),
            updateTimerId: null,
        }
    },
    computed: {
        notificationSections() {
            return [
                { label: this.$t('components.service.Informator.5'), value: 'new', count: this.$store.getters['informator/unreadNotifications'].length },
                { label: this.$t('components.service.Informator.6'), value: 'archive', count: this.$store.getters['informator/readNotifications'].length }
            ];
        },
        isMobile() {
            return this.$store.state.resize.w <= 768;
        },
        notificationsListLength() {
            if(this.selectedTab === 'new') {
                return this.$store.getters['informator/unreadNotifications'].length;
            } else {
                return this.$store.getters['informator/readNotifications'].length;
            }
        }
    },
    mounted() {
        this.updateTimerId = setInterval(() => {
            this.currentTimestamp = Date.now();
        }, 60000); // обновляем каждую минуту
        this.$store.commit('SET_SHOW_NEW_NOTIFICATION', true);
    },
    beforeUnmount() {
        clearInterval(this.updateTimerId);
    },
    methods: {
        notificationBody(notification) {
            // Тело локализовано на сервере (message {ru, en}). Правило выбора:
            // язык кабинета ru → русский текст, любой другой язык → английский.
            // В legacy:false $i18n.locale — WritableComputedRef, поэтому
            // разворачиваем .value (как в includes/lib.js); иначе ключ-объект
            // не совпал бы с ru/en и тело всегда падало бы в английский.
            let body = notification.message;
            if (typeof body === 'string') {
                try {
                    body = JSON.parse(body);
                } catch {
                    return notification.message;
                }
            }
            if (body && typeof body === 'object') {
                const rawLocale = this.$i18n.locale;
                const locale = (rawLocale && rawLocale.value) || rawLocale;
                return locale === 'ru'
                    ? (body.ru || body.en || '')
                    : (body.en || body.ru || '');
            }
            return '';
        },
        getIconBgColor(group) {
            const hex = this.iconColors[group];
            if (!hex) return 'transparent';
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, 0.2)`;
        },
        getRelativeTime(timestamp) {
            const now = this.currentTimestamp;
            // ts_create может быть в секундах (Unix) или миллисекундах
            const ts = timestamp < 1e12 ? timestamp * 1000 : timestamp;
            const diff = now - ts;

            const seconds = Math.floor(diff / 1000);
            const minutes = Math.floor(seconds / 60);
            const hours = Math.floor(minutes / 60);
            const days = Math.floor(hours / 24);

            if (seconds < 60) {
                return this.$t('components.service.Informator.7');
            }

            if (minutes < 60) {
                return this.$t('components.service.Informator.8', { count: minutes });
            }

            if (hours < 24) {
                return this.$t('components.service.Informator.9', { count: hours });
            }

            if (days === 1) {
                return this.$t('components.service.Informator.10');
            }

            if (days < 7) {
                return this.$t('components.service.Informator.11', { count: days });
            }

            const date = new Date(ts);
            let day = date.getDate();
            let month = date.getMonth() + 1;
            let year = date.getFullYear();

            if (day < 10) {
                day = '0' + day;
            }
            if (month < 10) {
                month = '0' + month;
            }

            return `${day}.${month}.${year}`;
        },
        async markAsRead(code) {
            this.pendingCodes.push(code);
            try {
                const res = await this.axios.post('ReadNotification', { no_loader: true, data: { codes: [code] } });
                if (res.data.body.result == '0') {
                    this.$store.dispatch('informator/markAsRead', code);
                }
            } finally {
                this.pendingCodes = this.pendingCodes.filter(c => c !== code);
            }
        },
        async deleteNotification(code) {
            this.pendingCodes.push(code);
            try {
                const res = await this.axios.post('DeleteNotification', { no_loader: true, data: { codes: [code] } });
                if (res.data.body.result == '0') {
                    this.$store.dispatch('informator/deleteNotification', code);
                }
            } finally {
                this.pendingCodes = this.pendingCodes.filter(c => c !== code);
            }
        },
        async markAllAsRead() {
            this.bulkLoading = true;
            try {
                const res = await this.axios.post('ReadNotification', { no_loader: true, data: { codes: this.$store.getters['informator/unreadNotifications'].map(n => n.code) } });
                if (res.data.body.result == '0') {
                    this.$store.dispatch('informator/markAllAsRead');
                }
            } finally {
                this.bulkLoading = false;
            }
        },
        async deleteAllNotifications() {
            this.bulkLoading = true;
            try {
                const res = await this.axios.post('DeleteNotification', { no_loader: true, data: { codes: this.$store.getters['informator/readNotifications'].map(n => n.code) } });
                if (res.data.body.result == '0') {
                    this.$store.dispatch('informator/deleteAllReadNotifications');
                }
            } finally {
                this.bulkLoading = false;
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.background-empty {
    background-image: url('@/assets/bg-empty-list.png');
    background-position: center;
}

.w32 {
    width: 32px;
}

.h32 {
    height: 32px;
}

.notification-circle {
    width: 40px;
    height: 40px;
    min-width: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.notification-item {
    padding: 12px 0;
    border-bottom: 1px solid $primary-stroke;

    &:hover .action-btn,
    .action-btn[aria-busy="true"] {
        visibility: visible;
        opacity: 1;
    }

    .action-btn {
        display: flex;
        visibility: hidden;
        opacity: 0;
        width: 32px;
        height: 32px;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        // .btn-паддинг бутстрапа (6×12px) не даёт спиннеру влезть в 32×32 квадрат —
        // обнуляем, чтобы крутилка (24px) центрировалась в кнопке и не выпирала.
        padding: 0;
    }
}
</style>